import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';

const ItemSuppliersTab = ({ itemId, editMode = false, createMode = false }) => {
  const { t } = useTranslation('procurement');

  const [itemSuppliers, setItemSuppliers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    provider_id: '',
    supplier_part_number: '',
    unit_price: '',
    lead_time_days: '',
    minimum_order_qty: '',
    currency: 'USD',
    is_preferred: false,
    notes: ''
  });

  const defaultFormData = {
    provider_id: '',
    supplier_part_number: '',
    unit_price: '',
    lead_time_days: '',
    minimum_order_qty: '',
    currency: 'USD',
    is_preferred: false,
    notes: ''
  };

  const fetchItemSuppliers = useCallback(async () => {
    if (!itemId || createMode) return;
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory/${itemId}/suppliers`));
      if (response.ok) {
        const data = await response.json();
        setItemSuppliers(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching item suppliers:', error);
      setItemSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, [itemId, createMode]);

  const fetchProviders = useCallback(async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/providers?page_size=200'));
      if (response.ok) {
        const data = await response.json();
        const providersList = data.providers || [];
        setProviders(Array.isArray(providersList) ? providersList : []);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      setProviders([]);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
    fetchItemSuppliers();
  }, [fetchProviders, fetchItemSuppliers]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openAddModal = () => {
    setEditingSupplier(null);
    setFormData({ ...defaultFormData });
    setShowModal(true);
  };

  const openEditModal = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      provider_id: supplier.provider_id || '',
      supplier_part_number: supplier.supplier_part_number || '',
      unit_price: supplier.unit_price != null ? supplier.unit_price : '',
      lead_time_days: supplier.lead_time_days != null ? supplier.lead_time_days : '',
      minimum_order_qty: supplier.minimum_order_qty != null ? supplier.minimum_order_qty : '',
      currency: supplier.currency || 'USD',
      is_preferred: supplier.is_preferred || false,
      notes: supplier.notes || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSupplier(null);
    setFormData({ ...defaultFormData });
  };

  const handleSaveSupplier = async () => {
    if (!formData.provider_id) return;

    const payload = {
      provider_id: parseInt(formData.provider_id),
      supplier_part_number: formData.supplier_part_number || null,
      unit_price: formData.unit_price !== '' ? parseFloat(formData.unit_price) : null,
      lead_time_days: formData.lead_time_days !== '' ? parseInt(formData.lead_time_days) : null,
      minimum_order_qty: formData.minimum_order_qty !== '' ? parseFloat(formData.minimum_order_qty) : null,
      currency: formData.currency || 'USD',
      is_preferred: formData.is_preferred,
      notes: formData.notes || null
    };

    try {
      let response;
      if (editingSupplier) {
        response = await apiFetch(
          getApiUrl(`/api/inventory/${itemId}/suppliers/${editingSupplier.item_supplier_id}`),
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }
        );
      } else {
        response = await apiFetch(
          getApiUrl(`/api/inventory/${itemId}/suppliers`),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }
        );
      }

      if (response.ok) {
        closeModal();
        fetchItemSuppliers();
      } else {
        const errorData = await response.json().catch(() => null);
        console.error('Error saving supplier:', errorData);
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    try {
      const response = await apiFetch(
        getApiUrl(`/api/inventory/${itemId}/suppliers/${supplierId}`),
        { method: 'DELETE' }
      );
      if (response.ok) {
        fetchItemSuppliers();
      }
    } catch (error) {
      console.error('Error deleting supplier:', error);
    }
  };

  const handleTogglePreferred = async (supplier) => {
    const payload = {
      provider_id: supplier.provider_id,
      supplier_part_number: supplier.supplier_part_number || null,
      unit_price: supplier.unit_price != null ? parseFloat(supplier.unit_price) : null,
      lead_time_days: supplier.lead_time_days != null ? parseInt(supplier.lead_time_days) : null,
      minimum_order_qty: supplier.minimum_order_qty != null ? parseFloat(supplier.minimum_order_qty) : null,
      currency: supplier.currency || 'USD',
      is_preferred: !supplier.is_preferred,
      notes: supplier.notes || null
    };

    try {
      const response = await apiFetch(
        getApiUrl(`/api/inventory/${itemId}/suppliers/${supplier.item_supplier_id}`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );
      if (response.ok) {
        fetchItemSuppliers();
      }
    } catch (error) {
      console.error('Error toggling preferred supplier:', error);
    }
  };

  const formatCurrency = (value, currency = 'USD') => {
    if (value == null || value === '') return '-';
    return parseFloat(value).toLocaleString('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    });
  };

  if (createMode) {
    return (
      <div className="tab-content">
        <div className="form-section">
          <h3>{t('materials.suppliers.title', 'Suppliers')}</h3>
          <p className="info-message">
            {t('materials.suppliers.availableAfterCreation', 'Suppliers can be added after the item is created.')}
          </p>
        </div>
      </div>
    );
  }

  const renderModal = () => (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {editingSupplier
              ? t('materials.suppliers.editSupplier', 'Edit Supplier')
              : t('materials.suppliers.addSupplier', 'Add Supplier')}
          </h2>
          <button className="btn-close" onClick={closeModal}>x</button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label>{t('materials.suppliers.fields.provider', 'Provider')} *</label>
              <select
                value={formData.provider_id}
                onChange={(e) => handleFormChange('provider_id', e.target.value)}
                required
              >
                <option value="">{t('materials.suppliers.selectProvider', '-- Select Provider --')}</option>
                {providers.map(provider => (
                  <option key={provider.provider_id} value={provider.provider_id}>
                    {provider.provider_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('materials.suppliers.fields.partNumber', 'Supplier Part #')}</label>
              <input
                type="text"
                value={formData.supplier_part_number}
                onChange={(e) => handleFormChange('supplier_part_number', e.target.value)}
                placeholder={t('materials.suppliers.placeholders.partNumber', 'e.g. SUP-12345')}
              />
            </div>
            <div className="form-group">
              <label>{t('materials.suppliers.fields.unitPrice', 'Unit Price')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.unit_price}
                onChange={(e) => handleFormChange('unit_price', e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="form-group">
              <label>{t('materials.suppliers.fields.currency', 'Currency')}</label>
              <select
                value={formData.currency}
                onChange={(e) => handleFormChange('currency', e.target.value)}
              >
                <option value="USD">USD</option>
                <option value="GTQ">GTQ</option>
                <option value="EUR">EUR</option>
                <option value="MXN">MXN</option>
              </select>
            </div>
            <div className="form-group">
              <label>{t('materials.suppliers.fields.leadTime', 'Lead Time (days)')}</label>
              <input
                type="number"
                min="0"
                value={formData.lead_time_days}
                onChange={(e) => handleFormChange('lead_time_days', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="form-group">
              <label>{t('materials.suppliers.fields.moq', 'Min. Order Qty')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.minimum_order_qty}
                onChange={(e) => handleFormChange('minimum_order_qty', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="form-group full-width">
              <label>{t('materials.suppliers.fields.notes', 'Notes')}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                placeholder={t('materials.suppliers.placeholders.notes', 'Additional notes about this supplier...')}
                rows="2"
              />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_preferred}
                  onChange={(e) => handleFormChange('is_preferred', e.target.checked)}
                />
                <span>{t('materials.suppliers.fields.preferred', 'Preferred Supplier')}</span>
              </label>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={closeModal}>
            {t('materials.suppliers.buttons.cancel', 'Cancel')}
          </button>
          <button
            className="btn-primary"
            onClick={handleSaveSupplier}
            disabled={!formData.provider_id}
          >
            {editingSupplier
              ? t('materials.suppliers.buttons.update', 'Update')
              : t('materials.suppliers.buttons.add', 'Add')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="tab-content">
      <div className="form-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>{t('materials.suppliers.title', 'Suppliers')}</h3>
          {(editMode) && (
            <button className="btn-primary" onClick={openAddModal}>
              + {t('materials.suppliers.addSupplier', 'Add Supplier')}
            </button>
          )}
        </div>

        {loading ? (
          <div className="loading">{t('materials.suppliers.loading', 'Loading suppliers...')}</div>
        ) : itemSuppliers.length > 0 ? (
          <div className="transactions-table">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('materials.suppliers.columns.providerName', 'Provider Name')}</th>
                  <th>{t('materials.suppliers.columns.partNumber', 'Part #')}</th>
                  <th style={{ textAlign: 'right' }}>{t('materials.suppliers.columns.unitPrice', 'Unit Price')}</th>
                  <th style={{ textAlign: 'center' }}>{t('materials.suppliers.columns.leadTime', 'Lead Time (days)')}</th>
                  <th style={{ textAlign: 'right' }}>{t('materials.suppliers.columns.moq', 'MOQ')}</th>
                  <th style={{ textAlign: 'center' }}>{t('materials.suppliers.columns.preferred', 'Preferred')}</th>
                  {editMode && (
                    <th style={{ textAlign: 'center' }}>{t('materials.suppliers.columns.actions', 'Actions')}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {itemSuppliers.map((supplier) => (
                  <tr key={supplier.item_supplier_id}>
                    <td>{supplier.supplier_name || supplier.provider_name || '-'}</td>
                    <td>{supplier.supplier_part_number || '-'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {formatCurrency(supplier.unit_price, supplier.currency)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {supplier.lead_time_days != null ? supplier.lead_time_days : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {supplier.minimum_order_qty != null
                        ? parseFloat(supplier.minimum_order_qty).toLocaleString()
                        : '-'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {editMode ? (
                        <button
                          className="btn-icon"
                          onClick={() => handleTogglePreferred(supplier)}
                          title={supplier.is_preferred
                            ? t('materials.suppliers.removePreferred', 'Remove preferred')
                            : t('materials.suppliers.setPreferred', 'Set as preferred')}
                          style={{ fontSize: '1.2rem', cursor: 'pointer', background: 'none', border: 'none' }}
                        >
                          {supplier.is_preferred ? '\u2605' : '\u2606'}
                        </button>
                      ) : (
                        <span style={{ fontSize: '1.2rem', color: supplier.is_preferred ? 'var(--warning, #f0ad4e)' : 'var(--text-secondary)' }}>
                          {supplier.is_preferred ? '\u2605' : '\u2606'}
                        </span>
                      )}
                    </td>
                    {editMode && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn-icon"
                          onClick={() => openEditModal(supplier)}
                          title={t('materials.suppliers.buttons.edit', 'Edit')}
                          style={{ marginRight: '0.5rem' }}
                        >
                          ✎
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDeleteSupplier(supplier.item_supplier_id)}
                          title={t('materials.suppliers.buttons.remove', 'Remove')}
                        >
                          ✕
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-data">
            <p>{t('materials.suppliers.noSuppliers', 'No suppliers linked to this item.')}</p>
          </div>
        )}
      </div>

      {showModal && renderModal()}
    </div>
  );
};

export default ItemSuppliersTab;
